import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Megaphone, X, AlertTriangle, Pin } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClub, useClubs } from '../hooks/useClubs';
import { useRoomMessages, useSendRoomMessage } from '../hooks/useRoomMessages';
import { type Message as ApiMessage, type SendMessagePayload } from '../lib/api/messages';
import { TopBar, ChatArea, ChatInputBar, EmptyState } from '../components';
import ContextSheet from '../components/ContextSheet';
import { type Message } from '../components/ChatMessage';
import { useClubRooms } from '../hooks/useRooms';
import { getReadOnlyBannerCopy } from '../lib/api/rooms';
import { useRoomActivity } from '../hooks/useRoomActivity';
import { toast } from 'sonner';
import { getUserFacingError } from '../lib/errorMessages';
import { oroErrorToastId, OroOperation, type ApiErrorEnvelope } from '../hooks/useOroMutation';
import { ChatTimeProvider } from '../contexts/ChatTimeContext';
import { useLoadingMessage } from '../hooks/useLoadingMessage';
import { useTour } from '../components/tour/useTour';
import { computeUnreadState } from '../lib/unreadDivider';
import {
  derivePinnedAnnouncement,
  selectServerPinnedAnnouncement,
  pickPinnedAnnouncement,
} from '../lib/pinnedAnnouncement';
import { getCssPxVar, getSafeAreaInsetBottom } from '../utils/css';

interface UploadedImage {
  id: string;
  url: string;
  file: File;
  isUploading: boolean;
}

// Transform API message to ChatMessage component format
function transformMessage(apiMsg: ApiMessage, currentUserId: string): Message {
  const isMyMessage = currentUserId === apiMsg.user_id;

  // Handle both single image_url and image_urls array (#609)
  const imageUrls = apiMsg.image_urls || (apiMsg.image_url ? [apiMsg.image_url] : undefined);

  return {
    id: apiMsg.id,
    type: isMyMessage ? 'self' : 'other',
    content: apiMsg.body,
    sender: apiMsg.user_name,
    senderId: apiMsg.user_id, // #920: For delete/report actions
    // Pass raw timestamp - ChatRelativeTime handles formatting + auto-updates
    // Issue #769: Removed getRelativeTime() call that required forceUpdate
    timestamp: '', // Legacy field - kept for type compatibility
    timestampSeconds: apiMsg.created_at,
    avatarUrl: apiMsg.profile_image_url || undefined,
    imageUrl: imageUrls?.[0] || undefined, // Legacy: first image
    imageUrls: imageUrls || undefined, // New: full array (#609)
    isAnnouncement: apiMsg.is_announcement, // #1445
    reactions: apiMsg.reactions, // #989 — pass the aggregation straight through
  };
}

// Fallback height for chat bar before ResizeObserver measures it
const DEFAULT_CHAT_BAR_HEIGHT = 70;

// #1611 (Frank P1 round 2): Find the latest server-confirmed message's
// created_at, skipping optimistic temp-* messages. Optimistic messages use
// client wall-clock time (Math.floor(Date.now()/1000) in useSendRoomMessage),
// which can race ahead of unfetched server messages — using their timestamp
// to advance lastReadAt would hide unfetched messages permanently. Anchoring
// to the latest non-temp message guarantees the marker only advances past
// content the user has demonstrably rendered from the server.
function getLatestServerMessageTimestamp(msgs: Message[]): number | undefined {
  for (let i = msgs.length - 1; i >= 0; i--) {
    const msg = msgs[i];
    if (msg && !msg.id.startsWith('temp-') && typeof msg.timestampSeconds === 'number') {
      return msg.timestampSeconds;
    }
  }
  return undefined;
}

function ClubChatPageImpl() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  
  // React Query hooks
  const { data: clubData, isLoading: clubLoading, error: clubError, refetch: refetchClub } = useClub(slug || '');
  const club = clubData?.club;
  const clubId = club?.id || '';

  // #1976 Phase 2: the context sheet offers club switching when the user belongs
  // to >1 club (ContextSheet renders the Clubs section only when clubs.length > 1).
  const { data: myClubs } = useClubs();
  
  // Room switcher state
  const [isContextSheetOpen, setIsContextSheetOpen] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);

  // Auto-open the context sheet when the guided tour reaches a step whose anchor
  // lives INSIDE the sheet (the rooms list, or the multi-club "Clubs" section,
  // #2032) so joyride can resolve it. The preceding "conversation-selector" step
  // points at the closed header trigger, so we deliberately do NOT open for it.
  const { currentStepTarget } = useTour();
  const [prevTourTarget, setPrevTourTarget] = useState(currentStepTarget);
  if (prevTourTarget !== currentStepTarget) {
    setPrevTourTarget(currentStepTarget);
    if (currentStepTarget === 'chat-rooms-list' || currentStepTarget === 'context-clubs') {
      setIsContextSheetOpen(true);
    }
  }

  // #1611: Single owner of room activity / read-state. ContextSheet /
  // RoomList receive hasNewActivity/isRecentlyActive as props to share it.
  const { hasNewActivity, isRecentlyActive, markAsRead, getLastReadAt } = useRoomActivity();
  
  // Fetch available rooms for this club
  const { 
    data: roomsData, 
    isLoading: roomsLoading, 
    error: roomsError 
  } = useClubRooms(clubId);
  
  const rooms = roomsData?.rooms || [];
  const lobbyRoom = rooms.find((r) => r.room_type === 'club_lobby');
  const activeRoom = rooms.find((r) => r.id === activeRoomId);
  const canWrite = activeRoom?.can_write ?? true; // Default to true for lobby

  // Derive subtitle for TopBar (#857, #1281: Leaders room shows "Leaders" not "Lobby")
  const roomSubtitle = activeRoom?.room_type === 'club_lobby'
    ? (activeRoom?.is_leaders_only ? 'Leaders' : 'Lobby')
    : activeRoom?.display_name || undefined;
  
  // Handle deep-link room selection from navigation state
  useEffect(() => {
    const state = location.state as { roomId?: string } | undefined;
    if (state?.roomId) {
      // Verify room exists in accessible rooms before selecting
      const targetRoom = rooms.find((r) => r.id === state.roomId);
      if (targetRoom) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: sync state with navigation
        setActiveRoomId(state.roomId);
      } else if (rooms.length > 0) {
        // Room not accessible (403) or not found (404) - fallback to lobby
        toast.error(getUserFacingError('ROOM_ACCESS_DENIED', 'You don\'t have access to that conversation'));
         
        setActiveRoomId(lobbyRoom?.id || rooms[0]?.id || null);
      }
      // Clear navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    } else if (!activeRoomId && lobbyRoom) {
      // Default to lobby on first load
       
      setActiveRoomId(lobbyRoom.id);
    }
  }, [location.state, rooms, lobbyRoom, activeRoomId]);
  
  // Monitor room fetch errors and handle fallback
  useEffect(() => {
    if (roomsError && lobbyRoom) {
      // If rooms fetch fails but we have lobby cached, fallback to it
      toast.error(getUserFacingError('ROOM_FETCH_FAILED', 'Unable to load conversations'));
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentional: fallback on error
      setActiveRoomId(lobbyRoom.id);
    }
  }, [roomsError, lobbyRoom]);
  
  // Room-based messaging (Issue #790)
  const {
    data: messagesData,
    isLoading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useRoomMessages(activeRoomId || '');

  // Only create sendMessage mutation when we have a valid roomId
  const sendMessageMutation = useSendRoomMessage(activeRoomId || '');

  // Custom loading message (#848)
  // #1011: Only show full-page loading for initial club/rooms load, not room switches
  const isInitialLoading = clubLoading || roomsLoading;
  const loadingMessage = useLoadingMessage(!!isInitialLoading);
  // Separate loading message for room switches (inline, TopBar stays visible)
  const roomSwitchLoadingMessage = useLoadingMessage(!!messagesLoading && !isInitialLoading);

  // Local UI state
  const [attachedImages, setAttachedImages] = useState<UploadedImage[]>([]);
  // #1445: id of the pinned announcement the user dismissed this session. Local,
  // no persistence (feedback_simple_solutions) — a newer announcement (different
  // id) reappears; the same one stays hidden until reload.
  const [dismissedAnnouncementId, setDismissedAnnouncementId] = useState<string | null>(null);
  // #1445: current time (unix seconds) for the derived 24h pin window. Lazy
  // initializer keeps the impure Date.now() read out of render (react-hooks/purity),
  // refreshed on an interval so the pin expires during a long-lived session —
  // same pattern as ClubEventsPage's currentTimestamp.
  const [nowSeconds, setNowSeconds] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const interval = window.setInterval(
      () => setNowSeconds(Math.floor(Date.now() / 1000)),
      60_000,
    );
    return () => window.clearInterval(interval);
  }, []);
  const [chatBarHeight, setChatBarHeight] = useState(DEFAULT_CHAT_BAR_HEIGHT);
  // #672: iOS bottom safe-area inset (home-indicator). Measured dynamically so
  // the last message isn't clipped behind the indicator on iPhone X+. 0 on
  // non-notched devices. Re-measured on resize/orientation change below.
  const [safeAreaBottom, setSafeAreaBottom] = useState(getSafeAreaInsetBottom);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const chatBarRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef(0);
  // #1611: Track which room we've done first-load scroll for, so room switches
  // (and re-entering a room) re-run the jump-to-bottom logic.
  const initializedForRoomRef = useRef<string | null>(null);

  // Track scroll height before loading older messages (#863)
  const scrollHeightBeforeRef = useRef<number>(0);
  const isLoadingOlderRef = useRef(false);

  // #1611: Dwell timer for mark-as-read. User must be at-bottom for 1.5s for
  // the room to count as "read" — peeking doesn't clear unread state.
  const DWELL_MS = 1500;
  const BOTTOM_THRESHOLD_PX = 100;
  const dwellTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // Cancel a pending dwell timer on unmount (slug-keyed remount, route exit, etc.)
    return () => {
      if (dwellTimerRef.current !== null) {
        window.clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }
    };
  }, []);

  // #1611 (Frank P1): Read messages from a ref so the dwell timer's deferred
  // callback fires with the LATEST rendered set, not the snapshot at schedule
  // time. Stale closure would otherwise advance lastReadAt to an older
  // timestamp than what the user has actually seen by the time the timer fires.
  // The ref is populated by an effect later in the component once `messages`
  // is in scope (see "messagesRef sync" below).
  const messagesRef = useRef<Message[]>([]);

  // #2264: The dwell timeout's cross-room guard must compare against the
  // LIVE room id. Comparing two values from the same render closure was
  // vacuous — and 4 of 5 setActiveRoomId paths (deep-link, initial
  // resolution, lobby/error fallbacks) don't clear a pending timer, so a
  // mid-dwell switch marked the OLD room read at the NEW room's newest
  // timestamp (permanent via markAsRead's no-regress guard).
  const activeRoomIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeRoomIdRef.current = activeRoomId;
  }, [activeRoomId]);

  // #1611 (Frank P2): At-bottom dwell scheduling extracted so it can be invoked
  // from the layout effect after programmatic positioning, not just from real
  // scroll events. Programmatic scrollIntoView({ block: 'start' }) doesn't fire
  // scroll events, and a room whose content fits the viewport never fires
  // any scroll event — leaving the dwell timer never scheduled.
  const scheduleDwellMarkAsRead = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !activeRoomId) return;

    const isAtBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < BOTTOM_THRESHOLD_PX;

    if (!isAtBottom) {
      if (dwellTimerRef.current !== null) {
        window.clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }
      return;
    }

    if (dwellTimerRef.current !== null) return; // already scheduled

    const roomIdAtSchedule = activeRoomId;
    dwellTimerRef.current = window.setTimeout(() => {
      dwellTimerRef.current = null;
      // Refuse to mark a different room than the one we scheduled for —
      // against the ref (live value), not the render closure (#2264).
      if (roomIdAtSchedule !== activeRoomIdRef.current) return;
      // Read fresh messages from the ref, not the closure-captured array.
      const latestServer = getLatestServerMessageTimestamp(messagesRef.current);
      if (typeof latestServer === 'number') {
        markAsRead(roomIdAtSchedule, latestServer);
      }
    }, DWELL_MS);
  }, [activeRoomId, markAsRead]);

  // Scroll handler for infinite scroll (#863) + dwell-at-bottom mark-as-read (#1611)
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Load more when scrolled near top (within 150px of top)
    const nearTop = container.scrollTop < 150;

    if (nearTop && hasNextPage && !isFetchingNextPage && !isLoadingOlderRef.current) {
      // Store scroll height before loading for position preservation
      scrollHeightBeforeRef.current = container.scrollHeight;
      isLoadingOlderRef.current = true;
      fetchNextPage();
    }

    scheduleDwellMarkAsRead();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, scheduleDwellMarkAsRead]);

  // Preserve scroll position after older messages load (#863)
  useLayoutEffect(() => {
    if (!isLoadingOlderRef.current || isFetchingNextPage) return;

    const container = scrollContainerRef.current;
    if (!container || scrollHeightBeforeRef.current === 0) {
      isLoadingOlderRef.current = false;
      return;
    }

    // Calculate height difference and adjust scroll position
    const scrollHeightAfter = container.scrollHeight;
    const heightDiff = scrollHeightAfter - scrollHeightBeforeRef.current;

    if (heightDiff > 0) {
      container.scrollTop += heightDiff;
    }

    // Reset tracking refs
    scrollHeightBeforeRef.current = 0;
    isLoadingOlderRef.current = false;
  }, [messagesData, isFetchingNextPage]);

  // Flatten all pages of messages into a single array
  const apiMessages = messagesData?.pages.flatMap(page => page.messages) || [];

  // #1445/#2674: the pinned-announcement selection lives in the tested
  // lib/pinnedAnnouncement helpers (extracted from this component in the r1
  // review — #4). The DERIVED scan (fast path over loaded pages) has a paging
  // blind spot; the SERVER pin fills it. pickPinnedAnnouncement takes the NEWEST
  // valid candidate from EITHER source (r2 review — George #1): the server pin
  // wins the blind-spot case, a just-posted derived pin wins over a stale server
  // `null` so the banner shows without waiting for a poll.
  // selectServerPinnedAnnouncement takes the FRESHEST page (this component reads
  // `select`-reversed pages, so `pages[0]` is the raw-last / freshest response —
  // the r1 #1 fix); pickPinnedAnnouncement then applies the 24h client cutoff
  // uniformly (r1 #2) using the 60s-ticked `nowSeconds`, so the banner drops at
  // 24h without waiting for a refetch. Deterministic winner (created_at DESC, id
  // DESC) matches mobile + the server read order.
  const derivedPinnedAnnouncement = derivePinnedAnnouncement(apiMessages, nowSeconds);
  const serverPinnedAnnouncement = selectServerPinnedAnnouncement(messagesData?.pages ?? []);
  const pinnedAnnouncement = pickPinnedAnnouncement(
    serverPinnedAnnouncement,
    derivedPinnedAnnouncement,
    nowSeconds,
  );
  const showPinnedBanner =
    !!pinnedAnnouncement && pinnedAnnouncement.id !== dismissedAnnouncementId;

  // #2708: the room's TRUE pin, resolved server-side and authoritative (no
  // client derivation). `select` reverses pages, so pages[0] is the freshest
  // response; the value is the same on every page (room-scoped, cursor-
  // independent). Coexistence (feature doc §Coexistence): when the pin IS the
  // shown announcement (same id) the announcement banner already carries it —
  // dedupe by suppressing the separate slim pin row.
  const pinnedMessage = messagesData?.pages?.[0]?.pinnedMessage ?? null;
  const pinDedupedIntoAnnouncement =
    !!pinnedMessage && showPinnedBanner && !!pinnedAnnouncement &&
    pinnedMessage.id === pinnedAnnouncement.id;
  const showPinBanner = !!pinnedMessage && !pinDedupedIntoAnnouncement;


  // Transform API messages to ChatMessage format
  const messages: Message[] = apiMessages.map(msg => 
    transformMessage(msg, currentUser?.id || '')
  );

  // Get last message for dependency optimization
  const lastMessage = messages[messages.length - 1];

  // messagesRef sync — keeps the ref pointing at the latest messages so the
  // dwell timer (and other deferred callbacks) read fresh state when they fire.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // #1611 + #1861: Unread divider state and first-load scroll target.
  // Both bug fixes (own-message exclusion, fully-unread fallback to bottom)
  // live in the pure computeUnreadState utility — see lib/unreadDivider.ts.
  const lastReadAt = activeRoomId ? getLastReadAt(activeRoomId) : undefined;
  const { firstUnreadId, showDivider, scrollTarget } = useMemo(
    () =>
      computeUnreadState({
        messages: messages.map((m) => ({
          id: m.id,
          timestampSeconds: m.timestampSeconds,
          type: m.type,
        })),
        lastReadAt,
        hasNextPage: !!hasNextPage,
      }),
    [messages, lastReadAt, hasNextPage],
  );
  const boundaryLoaded = !lastReadAt || !hasNextPage || (messages[0]?.timestampSeconds ?? 0) <= lastReadAt;

  // QA diagnostic (NOT telemetry): if user has more unread than first-page-size,
  // we suppress the divider and fall back to bottom. One-shot warn per room.
  const overflowWarnedForRoomRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      activeRoomId &&
      lastReadAt !== undefined &&
      !boundaryLoaded &&
      overflowWarnedForRoomRef.current !== activeRoomId
    ) {
      overflowWarnedForRoomRef.current = activeRoomId;
      console.warn(
        '[ClubChatPage] lastReadAt older than first page; divider suppressed',
        { activeRoomId, lastReadAt, oldestRendered: messages[0]?.timestampSeconds },
      );
    }
  }, [activeRoomId, lastReadAt, boundaryLoaded, messages]);

  // Smart auto-scroll: on first load follows scrollTarget from
  // computeUnreadState (bottom or unread anchor — see #1861); on new messages
  // auto-scrolls only when the user sent or is near bottom.
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const isNewMessage = messages.length > prevMessagesLengthRef.current;
    const isFromUser = lastMessage?.type === 'self';

    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;

    const scrollToBottom = () => {
      bottomRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    };

    // On first load for this room, follow scrollTarget. bottomRef guard
    // prevents flipping the init flag when the target node hasn't mounted
    // yet (#1611).
    if (
      activeRoomId &&
      initializedForRoomRef.current !== activeRoomId &&
      messages.length > 0 &&
      bottomRef.current
    ) {
      if (scrollTarget.kind === 'unread-anchor') {
        const anchor = document.getElementById(`msg-${scrollTarget.messageId}`);
        if (anchor) {
          anchor.scrollIntoView({ block: 'start' });
        } else {
          // DOM node not mounted yet — fall back to bottom and let the next
          // render attempt land on the divider.
          scrollToBottom();
          // Don't flip the init flag; retry on next render.
          prevMessagesLengthRef.current = messages.length;
          return;
        }
      } else {
        scrollToBottom();
      }
      initializedForRoomRef.current = activeRoomId;
      // Frank P2: schedule dwell here so a room that already fits the viewport
      // (no scroll events ever fire) can still mark-read after the dwell window.
      scheduleDwellMarkAsRead();
    } else if (isNewMessage && (isFromUser || isNearBottom)) {
      // Auto-scroll only if:
      // 1. User sent the message (optimistic update), OR
      // 2. User is already near bottom (watching conversation)
      scrollToBottom();
      // Frank P2: cover the smooth-scroll case where onScroll might not be
      // called for the programmatic scroll (or where it is but the dwell
      // re-schedule is desired at the new bottom anchor).
      scheduleDwellMarkAsRead();
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, lastMessage?.id, lastMessage?.type, activeRoomId, scrollTarget, scheduleDwellMarkAsRead]);

  // Measure chat bar height dynamically (handles image attachments growing the bar)
  useEffect(() => {
    const el = chatBarRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use modern borderBoxSize for sub-pixel accuracy, fallback to offsetHeight
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
        setChatBarHeight(height);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [attachedImages.length]); // Re-observe when attachments change

  // #672: Re-measure the iOS bottom safe-area inset on resize/orientation
  // change. The inset differs between portrait and landscape, and Safari's
  // collapsing toolbar changes the visual viewport. Layout-measurement only —
  // no data fetching.
  useEffect(() => {
    const remeasure = () => setSafeAreaBottom(getSafeAreaInsetBottom());
    window.addEventListener('resize', remeasure);
    window.addEventListener('orientationchange', remeasure);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('orientationchange', remeasure);
    };
  }, []);

  // Issue #769: Removed forceUpdate interval - ChatTimeProvider now handles time updates
  // Only the ChatRelativeTime components re-render, not the entire message tree

  const handleSendMessage = async (message: string, isAnnouncement = false): Promise<boolean> => {
    if ((!message.trim() && attachedImages.length === 0) || !club || !activeRoomId || sendMessageMutation.isPending) return false;

    // Check if any images are still uploading
    const isUploading = attachedImages.some(img => img.isUploading);
    if (isUploading) return false;

    try {
      const payload: SendMessagePayload = {
        body: message.trim() || '',
      };

      // #1445: only send the flag when true. Omitting it (vs sending false) keeps
      // ordinary sends byte-identical to the pre-announcement contract. The server
      // enforces role/tier/rate-limit gates; gate errors surface via the existing
      // useOroMutation + Sonner toast (canonical messages, no custom modal).
      if (isAnnouncement) {
        payload.is_announcement = true;
      }

      // Add images if available (backend expects image_urls array)
      const uploadedImageUrls = attachedImages
        .filter(img => !img.isUploading)
        .map(img => img.url);
      
      if (uploadedImageUrls.length > 0) {
        payload.image_urls = uploadedImageUrls;
      }
      
      const response = await sendMessageMutation.mutateAsync(payload);

      // Clear attached images after successful send
      setAttachedImages([]);

      // #1611 (Frank P1 round 3): Use the server response's created_at as the
      // authoritative timestamp for marking the room read. This is the only
      // deterministic source — reading messagesRef can race against onSuccess
      // committing the temp→server swap, and a quick room switch / unmount
      // can clear the dwell timer before it has a chance to catch up,
      // leaving the user's own newly-sent message looking unread to themselves.
      if (activeRoomId && typeof response?.message?.created_at === 'number') {
        markAsRead(activeRoomId, response.message.created_at);
      }
      return true;
    } catch (err) {
      // Error toast is handled by useOroMutation. #1445 Fix 5(f): the
      // announcement 3/day cap returns RATE_LIMITED, which the canonical error
      // map flattens to a generic "wait a moment" — misleading for a 24h cap.
      // Only for a failed ANNOUNCEMENT send, override the wrapper's toast IN
      // PLACE (same de-dup id, via the shared oroErrorToastId) with the server's
      // specific "3 per day" copy. Ordinary chat 429s keep the canonical copy;
      // the canonical map itself is untouched.
      const e = err as ApiErrorEnvelope;
      const code = e?.error?.code ?? e?.code;
      const serverMsg = e?.error?.message ?? e?.message;
      if (isAnnouncement && code === 'RATE_LIMITED' && serverMsg) {
        toast.error(serverMsg, {
          id: oroErrorToastId(OroOperation.SendRoomMessage, e?.status ?? 429, code),
        });
      }
      // Signal failure so the composer restores text + announcement arming.
      return false;
    }
  };

  const handleRemoveImage = (imageId: string) => {
    setAttachedImages(prev => prev.filter(img => img.id !== imageId));
  };

  // #1611: Switch handler. If the user was at the bottom of the current room
  // when they pick a different one, mark current as read before swapping.
  // This is the room-switch leg of the mark-as-read trigger set; it replaces
  // unmount-cleanup as the primary path (refs racing activeRoomId is bug-prone).
  const handleSelectRoom = useCallback((newRoomId: string) => {
    const container = scrollContainerRef.current;
    if (container && activeRoomId && activeRoomId !== newRoomId) {
      const isAtBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < BOTTOM_THRESHOLD_PX;
      if (isAtBottom) {
        // Frank P1: anchor to latest server-confirmed message timestamp.
        const latestServer = getLatestServerMessageTimestamp(messagesRef.current);
        if (typeof latestServer === 'number') markAsRead(activeRoomId, latestServer);
      }
    }
    if (dwellTimerRef.current !== null) {
      window.clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
    setActiveRoomId(newRoomId);
    setIsContextSheetOpen(false);
  }, [activeRoomId, markAsRead]);

  // #2808: retry affordance for the full-page terminal error (web mirror of
  // #2807's ClubChatScreen fix). Re-run the club fetch — which cascades to the
  // rooms query once a clubId resolves — and, when a room is already active,
  // the messages fetch too. A transient cold-open / reconnect blip is the
  // common failure this recovers from.
  const handleRetry = useCallback(() => {
    void refetchClub();
    if (activeRoomId) void refetchMessages();
  }, [refetchClub, refetchMessages, activeRoomId]);

  // Show loading while club, rooms, or messages are loading
  // Also wait for activeRoomId to be set (needs rooms to load first)
  if (loadingMessage) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
        <p className="text-sm text-text-muted">{loadingMessage}</p>
      </div>
    );
  }

  // #2220: TanStack sets `error` on failed BACKGROUND polls/fetchNextPage
  // while retaining cached data — the full-page error must only replace the
  // conversation when there is NO DATA at all. Gate on `!messagesData`, not
  // message count: a successfully-loaded EMPTY room is renderable (review P2
  // on PR #2274). With data present the conversation (and the ChatBar draft)
  // stays mounted; the next successful poll recovers. No per-poll toast: a
  // flaky connection polls every 30s and would toast every cycle.
  //
  // #2808: the terminal error requires a SETTLED error that left nothing to
  // render — the club query errored with no cached club, OR the messages query
  // errored with no cached messages. The old gate also carried a BARE `!club`
  // term, so a merely-unsettled / disabled / transiently-retrying club query
  // (no error object at all — e.g. the first render before useLoadingMessage's
  // effect sets showMessage, or an empty-slug disabled query) fell straight
  // into this dead-end (web mirror of the iOS #2780 "Unable to load
  // conversations" wall softened in #2807). That transient case now holds the
  // loader below; only a real settled error lands here, and it carries a
  // Try Again affordance that refetches instead of a one-way message.
  const clubLoadFailed = !!clubError && !club;
  const messagesLoadFailed = !!messagesError && !messagesData;
  if (clubLoadFailed || messagesLoadFailed) {
    return (
      <div className="flex flex-col h-full bg-oro-bg">
        <TopBar title="Chat" onBack={() => navigate(-1)} />
        {/* #3078: mirror the corrected #3057 wrapper — a `flex-1
            overflow-y-auto pt-14` scroll container with a padded content
            box — so the card clears the fixed 56px TopBar instead of
            rendering behind it (the old `flex items-center justify-center`
            branch carried no pt-14 offset). */}
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<AlertTriangle className="w-12 h-12" />}
              title="Couldn't load chat"
              // Frank r1: key the copy on the FAILED leg. `clubError?.message ||
              // messagesError?.message` showed the club's NON-terminal background
              // error when club is cached (clubLoadFailed false) but the messages
              // leg is the actual terminal trigger. Keying on which leg failed
              // shows the error that caused the dead-end.
              message={
                (clubLoadFailed ? clubError?.message : messagesError?.message) ||
                'Check your connection and try again.'
              }
              actionLabel="Try Again"
              onAction={handleRetry}
            />
          </div>
        </div>
      </div>
    );
  }

  // #2808: club data not yet available but NOT a settled error — the query is
  // in-flight, retrying, disabled (empty slug), or between states. Hold a loader
  // instead of the removed `!club` dead-end; once the query settles to an error
  // the terminal branch above takes over, and `club` is guaranteed defined past
  // this point.
  if (!club) {
    return (
      <div
        className="min-h-screen bg-oro-bg flex items-center justify-center p-4"
        data-testid="chat-loading"
      >
        <p className="text-sm text-text-muted">Loading chat…</p>
      </div>
    );
  }

  // Issue #769: ChatTimeProvider wraps chat content for self-updating timestamps
  // Provider scoped here (not App root) so interval only runs when chat is mounted
  return (
    <ChatTimeProvider>
      <div className="flex flex-col h-full bg-oro-bg">
        {/* Fixed header - always visible at top */}
        <TopBar
          title={club.name}
          onBack={() => navigate(-1)}
          conversationSelector={{
            label: roomSubtitle || 'Lobby',
            onOpen: () => setIsContextSheetOpen(true),
          }}
        />

        {/* #1976: header-opened context sheet (retired the #856 edge drawer).
            Phase 2 folds in club switching — the Clubs section shows only when the
            user has >1 club. #1611 read-state stays owned here; sheet + lists are
            presentational. */}
        <ContextSheet
          isOpen={isContextSheetOpen}
          onClose={() => setIsContextSheetOpen(false)}
          rooms={rooms}
          activeRoomId={activeRoomId}
          onSelectRoom={handleSelectRoom}
          isLoading={roomsLoading}
          error={roomsError}
          hasNewActivity={hasNewActivity}
          isRecentlyActive={isRecentlyActive}
          clubs={myClubs}
          activeClubSlug={slug}
          onSelectClub={(newSlug) => navigate(`/clubs/${newSlug}/chat`)}
        />

        {/* Scrollable messages area with padding for TopBar + chat input + bottom nav */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto pt-14"
          style={{
            // #672: chatBar + bottomNav + iOS safe-area-inset-bottom + breathing room.
            // bottomNav height read from the --oro-bottom-nav-height CSS var
            // (defined as 80px in index.css), with an 80px numeric fallback.
            // safeAreaBottom is the dynamically-measured home-indicator inset
            // (0 on non-notched devices) — without it the last ~10-40px of the
            // final message is clipped behind the indicator on iPhone X+.
            paddingBottom:
              (chatBarHeight || DEFAULT_CHAT_BAR_HEIGHT) +
              getCssPxVar('--oro-bottom-nav-height', 80) +
              safeAreaBottom +
              16,
          }}
          onScroll={handleScroll}
        >
          {/* #1445/#2674: 24h pinned announcement (server pin when present, else
              derived from loaded pages). Sticks just below the fixed TopBar
              (the --oro-top-bar-height token) so it stays visible while scrolling. Compact
              (Action-layer surface): Megaphone + truncated content + sender, tap
              to jump to the message (an honest toast when a server pin is beyond
              the loaded pages — the jump target isn't in the DOM yet; #2901 5a),
              local dismiss. */}
          {showPinnedBanner && pinnedAnnouncement && (
            <div
              data-testid="announcement-pinned-banner"
              className="sticky top-[var(--oro-top-bar-height)] z-[25] mx-3 mb-2 flex items-center gap-2 rounded-lg border border-accent/40 bg-oro-surface px-3 py-2 shadow-soft"
            >
              <Megaphone className="w-4 h-4 shrink-0 text-accent" />
              <button
                type="button"
                data-testid="announcement-pinned-jump"
                onClick={() => {
                  // #2901 (5a): a #2674 server pin can be beyond the loaded
                  // pages, so its msg-<id> isn't in the DOM — the bare
                  // scrollIntoView was then a SILENT no-op. Honest fallback (web
                  // twin of the mobile toast in #2874): tell the user instead.
                  const el = document.getElementById(`msg-${pinnedAnnouncement.id}`);
                  if (el) {
                    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                  } else {
                    toast.info('Announcement is older than loaded history');
                  }
                }}
                className="min-w-0 flex-1 text-left"
              >
                <span className="block truncate text-sm text-text-primary">
                  {pinnedAnnouncement.body || 'Announcement'}
                </span>
                <span className="block truncate text-xs text-text-muted">
                  {pinnedAnnouncement.user_name}
                </span>
              </button>
              <button
                type="button"
                data-testid="announcement-pinned-dismiss"
                aria-label="Dismiss announcement"
                onClick={() => setDismissedAnnouncementId(pinnedAnnouncement.id)}
                className="shrink-0 p-1 text-text-muted hover:text-text-primary transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {/* #2708: TRUE pin — a slim, persistent, silent "Pinned" row. When an
              announcement banner is also shown this renders directly beneath it
              (secondary slot, feature doc §Coexistence); when it IS the shown
              announcement it is deduped away. Tap to jump to the message. No
              dismiss (the pin is persistent until a leader unpins). */}
          {showPinBanner && pinnedMessage && (
            <div
              data-testid="pinned-message-banner"
              className={`sticky z-[24] mx-3 mb-2 flex items-center gap-2 rounded-lg border border-oro-border bg-oro-surface-alt px-3 py-1.5 shadow-soft ${
                // #2901 (5b): when the announcement banner is also shown this pin
                // row stacks directly beneath it. Derive the offset from the
                // --oro-top-bar-height token (top-14 == 3.5rem) plus the
                // announcement banner's own ~3rem footprint, rather than the
                // opaque 6.5rem magic number.
                showPinnedBanner
                  ? 'top-[calc(var(--oro-top-bar-height)_+_3rem)]'
                  : 'top-[var(--oro-top-bar-height)]'
              }`}
            >
              <Pin className="w-3.5 h-3.5 shrink-0 text-text-secondary" />
              <button
                type="button"
                data-testid="pinned-message-jump"
                onClick={() => {
                  // #2901 (5a): a #2674 server pin can be beyond the loaded
                  // pages, so its msg-<id> isn't in the DOM — the bare
                  // scrollIntoView was then a SILENT no-op. Honest fallback (web
                  // twin of the mobile toast in #2874): tell the user instead.
                  const el = document.getElementById(`msg-${pinnedMessage.id}`);
                  if (el) {
                    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
                  } else {
                    toast.info('Pinned message is older than loaded history');
                  }
                }}
                className="min-w-0 flex-1 text-left"
              >
                <span className="block truncate text-xs text-text-primary">
                  {pinnedMessage.body || 'Pinned message'}
                </span>
                <span className="block truncate text-[11px] text-text-muted">
                  Pinned · {pinnedMessage.user_name}
                </span>
              </button>
            </div>
          )}
          {/* Loading indicator for older messages (#863) */}
          {isFetchingNextPage && (
            <div className="flex justify-center py-3">
              <p className="text-xs text-text-muted">Loading older messages...</p>
            </div>
          )}
          {/* #2291: a failed load-older gesture was silent — indicator gone,
              history just stops. Tappable retry, scoped to the gesture via
              isFetchNextPageError (background poll failures stay quiet per
              #2220). Mirrors mobile's #2267 row. */}
          {!isFetchingNextPage && isFetchNextPageError && hasNextPage && (
            <div className="flex justify-center py-3">
              <button
                type="button"
                data-testid="chat-load-older-retry"
                className="text-xs text-oro-error min-h-11 px-3"
                onClick={() => {
                  const container = scrollContainerRef.current;
                  if (container) {
                    // Same position-preservation handshake as handleScroll.
                    scrollHeightBeforeRef.current = container.scrollHeight;
                    isLoadingOlderRef.current = true;
                  }
                  void fetchNextPage();
                }}
              >
                Couldn&apos;t load older messages — tap to retry
              </button>
            </div>
          )}
          {/* End of history indicator (#863) */}
          {!hasNextPage && messages.length > 0 && (
            <div className="flex justify-center py-3">
              <p className="text-xs text-text-tertiary">Beginning of conversation</p>
            </div>
          )}
          {/* #1011: Inline loading for room switches (TopBar stays visible) */}
          {roomSwitchLoadingMessage ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <p className="text-sm text-text-muted">{roomSwitchLoadingMessage}</p>
            </div>
          ) : (
            <ChatArea
              messages={messages}
              clubId={clubId}
              roomId={activeRoomId || undefined}
              firstUnreadId={showDivider ? firstUnreadId : null}
              // #2900: fail-closed reaction gate. `canWrite` defaults `?? true`
              // for the composer's lobby optimism (line ~126), but that leaks
              // TRUE while the active room is unresolved (activeRoom undefined) —
              // violating the reaction children's documented `canReact = false`
              // default. Gate reactions on the room's OWN resolved write access
              // so the affordance never renders before access is known. The
              // composer's lobby default is intentionally left untouched.
              canReact={activeRoom?.can_write ?? false}
              canPin={activeRoom?.can_pin ?? false}
              pinnedMessageId={pinnedMessage?.id ?? null}
              onScroll={() => {}}
            />
          )}
          <div ref={bottomRef} />
        </div>

        {/* Fixed input bar - glued to top of bottom nav */}
        {canWrite ? (
          <ChatInputBar
            containerRef={chatBarRef}
            onSendMessage={handleSendMessage}
            attachedImages={attachedImages}
            onAttachedImagesChange={setAttachedImages}
            onRemoveImage={handleRemoveImage}
            clubId={clubId}
            isSending={sendMessageMutation.isPending}
            canAnnounce={activeRoom?.can_announce ?? false}
          />
        ) : (
          /* Read-only mode (#784 group rooms; copy names the real blocker per
             room type since #2479 dropped the event/series RSVP write gate) */
          <div
            ref={chatBarRef}
            data-testid="read-only-banner"
            className="fixed left-0 right-0 bg-oro-surface border-t border-oro-border px-4 py-3"
            style={{ bottom: 80 }}
          >
            <p className="text-center text-text-secondary text-sm">
              {getReadOnlyBannerCopy(activeRoom?.room_type)}
            </p>
          </div>
        )}
      </div>
    </ChatTimeProvider>
  );
}

// #1598: Remount on club slug change so local state (activeRoomId,
// attachedImages, scroll refs) doesn't leak from the previous club.
export default function ClubChatPage() {
  const { slug } = useParams<{ slug: string }>();
  return <ClubChatPageImpl key={slug ?? 'no-slug'} />;
}
